import * as Sentry from '@sentry/react';
import { yayQuestion } from '_constants/questionTreeConstants';
import { useReportTrialStartedMutation } from '_services/Api/Membership/MembershipApi';
import UserApi from '_services/Api/User';
import usePremiumInfo from '_services/Redux/User/Selector/usePremiumInfo';
import useUser from '_services/Redux/User/Selector/useUser';
import { getUser } from '_services/Redux/User/UserSlice';
import ReferralContextService from '_services/Referral';
import ReferralAttributionService from '_services/Referral/ReferralAttributionService';
import { OnboardingPageType } from '_types/question';
import trackTrialStartFromCheckoutSuccess from 'Components/FacebookPixel/trackTrialStartFromCheckoutSuccess';
import useNavigate from 'Hooks/useNavigate';
import ImageQuestionScreen from 'Pages/Guest/Onboarding/QuestionScreens/Image/ImageQuestionScreen';
import SubscriptionOnboardingChoice from 'Pages/Guest/Onboarding/QuestionScreens/SubscriptionOnboardingChoice/SubscriptionOnboardingChoice';
import SummerOfferJune2026Screen from 'Pages/Guest/Onboarding/QuestionScreens/SummerOfferJune2026/SummerOfferJune2026Screen';
import TrialInformationScreen from 'Pages/Guest/Onboarding/QuestionScreens/Trial/TrialInformationScreen';
import TrialNotificationScreen from 'Pages/Guest/Onboarding/QuestionScreens/Trial/TrialNotificationScreen';
import TrialScreen from 'Pages/Guest/Onboarding/QuestionScreens/Trial/TrialScreen';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

export type CampaignCurrency = 'NOK' | 'USD' | 'GBP' | 'EUR';

const OnboardingOfferPage = (): React.JSX.Element => {
    const navigate = useNavigate();
    const { user } = useUser();
    const dispatch = useDispatch();
    const [reportTrialStarted] = useReportTrialStartedMutation();
    const { isInJune2026SummerCampaign } = usePremiumInfo();

    const [questionType, setQuestionType] = useState<OnboardingPageType>(() => {
        if (typeof window === 'undefined') return 'trialInformation';
        const hasSubscribed =
            new URLSearchParams(window.location.search).get(
                'checkoutSuccess',
            ) === 'true';
        return hasSubscribed ? 'image' : 'trialInformation';
    });
    const [campaignStep] = useState<
        | 'startTheYear'
        | 'knittingGift'
        | 'januaryOffer'
        | 'yay'
        | 'summer_offer_june2026'
    >(() => {
        if (typeof window === 'undefined') return 'startTheYear';
        const hasGoneBack =
            new URLSearchParams(window.location.search).get('hasGoneBack') ===
            'true';
        const hasSubscribed =
            new URLSearchParams(window.location.search).get(
                'checkoutSuccess',
            ) === 'true';
        return hasGoneBack
            ? 'januaryOffer'
            : hasSubscribed
              ? 'yay'
              : 'startTheYear';
    });

    const goHomeOrToAppInfo = () => {
        navigate('/useapp');
    };

    useEffect(() => {
        if (questionType === 'exit') {
            handleExit();
        }
    }, [questionType, !!user]);

    const handleExit = () => {
        if (user) {
            // Premium status might have changed; refetch user then navigate
            (async () => {
                try {
                    //Force refresh
                    await UserApi.refreshMembership();
                    await dispatch(getUser()).unwrap();

                    goHomeOrToAppInfo();
                } catch (e) {
                    Sentry.captureException(e, {
                        level: 'warning',
                    });
                    goHomeOrToAppInfo();
                }
            })();
        }
    };

    /** disable scrolling on body, as set by app.tsx. Reset by BaseLayout.tsx    */
    useEffect(() => {
        document.body.classList.add('full-screen');
    }, []);

    useEffect(() => {
        void ReferralAttributionService.trackOnboardingOfferShown();
    }, []);

    useEffect(() => {
        if (trackTrialStartFromCheckoutSuccess()) {
            reportTrialStarted();
        }
    }, [reportTrialStarted]);

    if (campaignStep === 'yay') {
        // Clear referral context after sucessful start of subscription, to avoid
        // showing "you already have full access" modal
        ReferralContextService.clear();
        return (
            <ImageQuestionScreen
                question={yayQuestion}
                onAnswer={() => {
                    handleExit();
                }}
                isReboarding={false}
            />
        );
    }

    switch (questionType) {
        // Step 2: Slik fungerer prøveperioden
        case 'trial':
            return (
                <TrialScreen
                    onAnswer={(idx) => {
                        if (idx[0] === 42) setQuestionType('exit');
                        else {
                            setQuestionType('trial_notification');
                        }
                    }}
                />
            );
        // Step 3: Du får påminnelse 2 dager før prøveperioden din utløper
        case 'trial_notification':
            return (
                <TrialNotificationScreen
                    onAnswer={(idx) => {
                        if (idx[0] === 42) setQuestionType('exit');
                        else setQuestionType('subscription_choice');
                    }}
                />
            );

        // Step 4: Velg abonnement
        case 'subscription_choice':
            return (
                <SubscriptionOnboardingChoice
                    onAnswer={(idx) => {
                        if (idx[0] === 42) handleExit();
                        else {
                            if (isInJune2026SummerCampaign) {
                                setQuestionType('summer_offer_june2026');
                                return;
                            }
                            goHomeOrToAppInfo();
                        }
                    }}
                />
            );

        // optional step 5 for june 2026 campaign: show summer offer after user has accepted subscription choice, but before they see the "you already have access" modal. Only shown if user is eligible for june 2026 campaign, and has not accepted subscription choice
        case 'summer_offer_june2026':
            return (
                <SummerOfferJune2026Screen
                    onAnswer={() => goHomeOrToAppInfo()}
                />
            );

        // fallback if user goes back in flow, or if questionType is not set for some reason. Should not happen, but good to have a fallback that does not break the page

        // Step 1: Velkomstgave til deg som er ny 🎉
        case 'trialInformation':
        default:
            return (
                <TrialInformationScreen
                    onAnswer={(idx) => {
                        if (idx[0] === 42) setQuestionType('exit');
                        else setQuestionType('trial');
                    }}
                />
            );
    }
};

export default OnboardingOfferPage;
